import { useCallback, useRef, useState } from "react";
import { config } from "../config";

export type RecordingState = "idle" | "recording" | "transcribing" | "error";

export interface VoiceTranscriptionResult {
  text: string;
  noSpeech: boolean;
}

// Prefer simpler codecs first — webm;codecs=opus can fail on some platforms
const CANDIDATE_MIME_TYPES = ["audio/webm", "audio/mp4", "audio/webm;codecs=opus"];
const CHUNK_INTERVAL_MS = 4000;

function pickSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const chosen = CANDIDATE_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
  if (chosen) console.debug("[voice] Recording with MIME type:", chosen);
  return chosen;
}

function fileExtForMimeType(mimeType: string): string {
  if (mimeType.includes("mp4")) return "m4a";
  return "webm";
}

function apiBase(): string {
  return config.API_URL.replace(/\/+$/, "");
}

async function uploadChunk(sessionId: string, blob: Blob, mimeType?: string): Promise<void> {
  try {
    const ext = fileExtForMimeType(mimeType || "audio/webm");
    console.debug(`[voice] Uploading chunk: ${blob.size} bytes (${mimeType || "unknown"})`);
    const formData = new FormData();
    formData.append("session_id", sessionId);
    formData.append("file", blob, `chunk.${ext}`);
    await fetch(`${apiBase()}/chat_with_voice/chunk`, { method: "POST", body: formData });
  } catch {
    // Best-effort — a dropped chunk doesn't interrupt local recording; /finish
    // will simply have to transcribe more of the audio in one go.
  }
}

export function useVoiceRecording() {
  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCount, setErrorCount] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mimeTypeRef = useRef<string>("audio/webm");
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const finishAbortControllerRef = useRef<AbortController | null>(null);
  // Holds the most recently captured chunk that hasn't been uploaded yet.
  // Only sent to /chunk once we know a *newer* chunk exists (i.e. it wasn't
  // the last one) — the one still pending when recording stops is exactly
  // the final chunk, sent once via /finish instead of /chunk.
  const pendingChunkRef = useRef<Blob | null>(null);

  const isSupported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  const setError = (msg: string | null) => {
    setErrorMessage(msg);
    if (msg) setErrorCount(prev => prev + 1);
  };

  const teardownAnalyser = () => {
    analyserRef.current = null;
    const audioContext = audioContextRef.current;
    audioContextRef.current = null;
    if (audioContext && audioContext.state !== "closed") {
      audioContext.close().catch(() => {});
    }
  };

  const setupAnalyser = (stream: MediaStream) => {
    try {
      const AudioContextClass =
        window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) return;

      const audioContext = new AudioContextClass();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
    } catch {
      // Live waveform is best-effort — recording still works without it.
    }
  };

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError("O seu browser não suporta gravação de áudio.");
      setRecordingState("error");
      return;
    }

    setErrorMessage(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setupAnalyser(stream);

      const startResponse = await fetch(`${apiBase()}/chat_with_voice/start`, { method: "POST" });
      if (!startResponse.ok) {
        throw new Error(`Falha ao iniciar sessão de voz (status ${startResponse.status})`);
      }
      const { session_id } = await startResponse.json();
      sessionIdRef.current = session_id;
      pendingChunkRef.current = null;

      const mimeType = pickSupportedMimeType() ?? "audio/webm";
      mimeTypeRef.current = mimeType;

      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (event) => {
        if (event.data.size === 0) return;
        const previousPending = pendingChunkRef.current;
        pendingChunkRef.current = event.data;
        if (previousPending && sessionIdRef.current) {
          uploadChunk(sessionIdRef.current, previousPending, mimeTypeRef.current);
        }
      };
      mediaRecorderRef.current = recorder;

      recorder.start(CHUNK_INTERVAL_MS);
      setRecordingState("recording");
    } catch {
      setError("Não foi possível iniciar a gravação. Verifique o acesso ao microfone e a ligação.");
      setRecordingState("error");
    }
  }, [isSupported]);

  const stopRecording = useCallback((): Promise<VoiceTranscriptionResult> => {
    return new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;
      const sessionId = sessionIdRef.current;
      if (!recorder || !sessionId) {
        const error = new Error("Nenhuma gravação em curso.");
        setErrorMessage(error.message);
        setRecordingState("error");
        reject(error);
        return;
      }

      recorder.onstop = async () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        teardownAnalyser();
        setRecordingState("transcribing");

        const abortController = new AbortController();
        finishAbortControllerRef.current = abortController;

        try {
          const finalChunk = pendingChunkRef.current ?? new Blob([], { type: mimeTypeRef.current });
          pendingChunkRef.current = null;

          const formData = new FormData();
          formData.append("session_id", sessionId);
          formData.append("file", finalChunk, "chunk.webm");

          const response = await fetch(`${apiBase()}/chat_with_voice/finish`, {
            method: "POST",
            body: formData,
            signal: abortController.signal,
          });

          if (!response.ok) {
            throw new Error(`Falha na transcrição (status ${response.status})`);
          }

          const data = await response.json();
          sessionIdRef.current = null;
          setRecordingState("idle");
          resolve({ text: data.text ?? "", noSpeech: !!data.no_speech });
        } catch (err) {
          sessionIdRef.current = null;
          if (err instanceof Error && err.name === "AbortError") {
            // User-initiated cancel — no error message, back to idle silently.
            setRecordingState("idle");
          } else {
            const message = err instanceof Error ? err.message : "Falha na transcrição.";
            setErrorMessage(message);
            setRecordingState("error");
          }
          reject(err instanceof Error ? err : new Error("Falha na transcrição."));
        } finally {
          finishAbortControllerRef.current = null;
        }
      };

      recorder.stop();
    });
  }, []);

  const cancelRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.stop();
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    teardownAnalyser();
    mediaRecorderRef.current = null;

    const sessionId = sessionIdRef.current;
    sessionIdRef.current = null;
    pendingChunkRef.current = null;
    if (sessionId) {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      fetch(`${apiBase()}/chat_with_voice/cancel`, { method: "POST", body: formData }).catch(() => {});
    }

    setRecordingState("idle");
  }, []);

  const cancelTranscription = useCallback(() => {
    finishAbortControllerRef.current?.abort();
  }, []);

  return {
    recordingState,
    errorMessage,
    errorCount,
    isSupported,
    startRecording,
    stopRecording,
    cancelRecording,
    cancelTranscription,
    analyserRef,
  };
}
